import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { GameCard, GameHero, LivesBadge } from "../../components/game";
import {
  getLivesPurchaseConfig,
  getLivesPurchaseHistory,
  createLivesCheckout,
  type LivesPurchaseConfig,
  type LivesPurchaseRow,
  type LivePackage,
} from "../../services/livesPurchase";

const fmtMoney = (cents: number, currency: string) =>
  `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency.toUpperCase()}`;

const statusStyle: Record<string, string> = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  failed: "bg-rose-100 text-rose-700",
  expired: "bg-slate-100 text-slate-600",
};

export default function LivesShop() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [config, setConfig] = useState<LivesPurchaseConfig | null>(null);
  const [history, setHistory] = useState<LivesPurchaseRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState<string | null>(null); // `${athleteId}:${packageId}`
  const [error, setError] = useState<string | null>(null);
  const purchaseFlag = params.get("lives_purchase"); // "success" | "cancel"

  useEffect(() => {
    (async () => {
      try {
        const [cfg, hist] = await Promise.all([getLivesPurchaseConfig(), getLivesPurchaseHistory()]);
        setConfig(cfg);
        setHistory(hist);
      } catch {
        setError(t("lives.load_error", "Nu am putut încărca magazinul de vieți."));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  // Cheapest price-per-life among active packages → "best value" badge.
  const bestValueId = useMemo(() => {
    if (!config?.packages?.length) return null;
    let best: LivePackage | null = null;
    for (const p of config.packages) {
      if (!best || p.price_cents / p.qty < best.price_cents / best.qty) best = p;
    }
    return best?.id ?? null;
  }, [config]);

  const buy = async (athleteId: string, pkg: LivePackage) => {
    const key = `${athleteId}:${pkg.id}`;
    setBuying(key);
    setError(null);
    try {
      const { url } = await createLivesCheckout(athleteId, pkg.id);
      window.location.href = url; // hand off to Stripe Checkout
    } catch (e: any) {
      setError(e?.response?.data?.error || t("lives.buy_error", "Nu am putut iniția plata."));
      setBuying(null);
    }
  };

  return (
    <MobileAppLayout>
      <div className="pb-24 max-w-2xl mx-auto">
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-4">
          <h1 className="text-xl font-extrabold">{t("lives.shop_title", "Vieți")}</h1>
          <p className="text-white/70 text-[13px] mt-0.5">
            {t("lives.shop_subtitle", "Cumpără pachete de vieți ca să continui quiz-urile fără să aștepți.")}
          </p>
        </GameHero>

      <div className="px-3 pt-4 space-y-4">
        {purchaseFlag === "success" && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
            {t("lives.purchase_success", "Plată reușită! Viețile apar în câteva momente după confirmare.")}
            <button className="ml-2 underline" onClick={() => setParams({})}>
              OK
            </button>
          </div>
        )}
        {purchaseFlag === "cancel" && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
            {t("lives.purchase_cancel", "Plata a fost anulată.")}
            <button className="ml-2 underline" onClick={() => setParams({})}>
              OK
            </button>
          </div>
        )}
        {error && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700">{error}</div>
        )}

        {loading ? (
          <GameCard className="p-4 text-sm text-slate-500">{t("common.loading", "Se încarcă...")}</GameCard>
        ) : !config?.enabled ? (
          <GameCard className="p-4 text-sm text-slate-600">
            {config?.disabled_reason === "no_packages"
              ? t(
                  "lives.disabled_no_packages",
                  "Nu există încă pachete de vieți definite. Administratorul platformei trebuie să le adauge."
                )
              : config?.disabled_reason === "stripe"
                ? t("lives.disabled_stripe", "Plățile nu sunt configurate momentan.")
                : t("lives.disabled", "Cumpărarea de vieți nu este disponibilă momentan.")}
          </GameCard>
        ) : config.athletes.length === 0 ? (
          <GameCard className="p-4 text-sm text-slate-600">
            {t(
              "lives.no_targets",
              "Nu poți cumpăra vieți din acest cont. Pentru sportivii minori, achiziția se face din contul părintelui/tutorelui."
            )}
          </GameCard>
        ) : (
          config.athletes.map((a) => (
            <GameCard key={a.athlete_id} className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-bold text-slate-800">
                  {a.is_self ? t("lives.your_account", "Contul tău") : a.name}
                </p>
                <div className="flex items-center gap-2">
                  <LivesBadge lives={a.lives} />
                  <span className="text-xs text-slate-500">
                    {a.lives}/{config.hard_cap}
                  </span>
                </div>
              </div>

              {a.headroom <= 0 ? (
                <p className="text-xs font-semibold text-slate-500">
                  {t("lives.at_cap", "Ai deja maximul de {{cap}} vieți.", { cap: config.hard_cap })}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {config.packages.map((pkg) => {
                    const fits = pkg.qty <= a.headroom;
                    const key = `${a.athlete_id}:${pkg.id}`;
                    const isBest = pkg.id === bestValueId;
                    return (
                      <button
                        key={pkg.id}
                        onClick={() => fits && buy(a.athlete_id, pkg)}
                        disabled={!fits || buying === key}
                        className={`relative text-left rounded-xl border p-3 transition-all ${
                          fits
                            ? "border-slate-200 hover:border-blue-400 hover:shadow-sm bg-white"
                            : "border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed"
                        }`}
                      >
                        {isBest && fits && (
                          <span className="absolute -top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                            {t("lives.best_value", "cel mai bun preț")}
                          </span>
                        )}
                        <div className="flex items-center gap-1.5">
                          <span className="text-xl font-extrabold text-slate-800">{pkg.qty}</span>
                          <span className="text-xs text-slate-500">{t("lives.lives", "vieți")}</span>
                        </div>
                        <div className="mt-1 text-sm font-bold text-blue-600">
                          {buying === key ? t("common.please_wait", "Așteaptă...") : fmtMoney(pkg.price_cents, pkg.currency)}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {fits
                            ? `${(pkg.price_cents / pkg.qty / 100).toFixed(2)} ${pkg.currency.toUpperCase()}/${t("lives.life", "viață")}`
                            : t("lives.no_fit", "nu încape")}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <p className="text-[11px] text-slate-400">
                {t("lives.wallet_note", "Standard 5 vieți (se refac gratuit). Cumpărate: maxim {{cap}}. Prețuri cu TVA inclus.", {
                  cap: config.hard_cap,
                })}
              </p>
            </GameCard>
          ))
        )}

        {/* History */}
        <div>
          <h2 className="px-1 mb-2 text-sm font-bold text-slate-700">
            {t("lives.history_title", "Istoric achiziții")}
          </h2>
          {history.length === 0 ? (
            <GameCard className="p-4 text-sm text-slate-500">
              {t("lives.history_empty", "Nicio achiziție încă.")}
            </GameCard>
          ) : (
            <div className="space-y-2">
              {history.map((r) => (
                <GameCard key={r.id} className="p-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm truncate">
                      {r.qty} {r.qty === 1 ? t("lives.life", "viață") : t("lives.lives", "vieți")}
                      {!r.is_self && r.athlete_name ? ` · ${r.athlete_name}` : ""}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(r.created_at).toLocaleDateString("ro-RO", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                      {" · "}
                      {fmtMoney(r.amount_cents, r.currency)}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                      statusStyle[r.status] || statusStyle.expired
                    }`}
                  >
                    {t(`lives.status_${r.status}`, r.status)}
                  </span>
                </GameCard>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </MobileAppLayout>
  );
}
